import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

interface BrakeItem {
  title: string;
  description: string;
  details: string[];
  image: string;
}

export default function ImpiantoFrenantePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'Sostituzione Freni a Brescia e Provincia - Pastiglie e Dischi Freno | Metamorfosi Preparazioni',
    description: 'Sostituzione freni a Brescia e provincia. Pastiglie e dischi freno per auto e moto a domicilio. Controllo completo dell\'impianto frenante, materiali di alta qualità. La sicurezza inizia dai freni. Servizio professionale certificato a Brescia.',
    keywords: 'sostituzione freni Brescia, pastiglie freno Brescia, dischi freno Brescia, impianto frenante Brescia, freni auto Brescia, freni moto Brescia, manutenzione freni Brescia, meccanico freni Brescia, freni provincia Brescia',
    canonical: '/impianto-frenante',
    ogTitle: 'Sostituzione Freni a Brescia - Pastiglie e Dischi Freno',
    ogDescription: 'Sostituzione e manutenzione impianto frenante a domicilio a Brescia',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Manutenzione Impianto Frenante a Brescia',
      provider: {
        '@type': 'AutoRepair',
        name: 'Metamorfosi Preparazioni',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
        telephone: '+39-351-831-2394',
        email: 'metamorfosi.preparazioni@gmail.com'
      },
      areaServed: {
        '@type': 'City',
        name: 'Brescia',
        containedIn: {
          '@type': 'AdministrativeArea',
          name: 'Provincia di Brescia'
        }
      },
      description: 'Sostituzione pastiglie e dischi freno per auto e moto a Brescia e provincia. Controllo completo dell\'impianto frenante con materiali di alta qualità per garantire la massima sicurezza stradale.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '$$'
      }
    }
  });

  const brakeItems: BrakeItem[] = [
    {
      title: 'Pastiglie Freno',
      description: 'Le pastiglie freno sono il componente che entra in contatto diretto con il disco per rallentare e fermare il veicolo. La loro usura è normale e richiede controlli periodici per garantire la massima sicurezza.',
      details: [
        'Materiali di alta qualità per frenate sicure',
        'Riduzione della distanza di frenata',
        'Minore rumorosità durante la frenata',
        'Intervallo consigliato: ogni 30.000-50.000 km',
        'Controllo visivo dello spessore ad ogni tagliando'
      ],
      image: 'https://public.readdy.ai/ai/img_res/8ad0e8c8b3afc13989acab831e6c48b9.jpg'
    },
    {
      title: 'Disco Freno',
      description: 'Il disco freno è il componente rotante su cui agiscono le pastiglie per generare la forza frenante. Un disco in buone condizioni garantisce frenate efficaci, progressive e sicure in ogni situazione.',
      details: [
        'Dissipazione ottimale del calore',
        'Superficie di frenata uniforme e liscia',
        'Resistenza alle alte temperature',
        'Intervallo consigliato: ogni 60.000-80.000 km',
        'Verifica dello spessore minimo e delle crepe'
      ],
      image: 'https://readdy.ai/api/search-image?query=New%20automotive%20brake%20disc%20rotor%20ventilated%20steel%20brake%20rotor%20shiny%20metallic%20surface%20professional%20car%20part%20clean%20product%20photography%20white%20background%20high%20detail%20automotive%20safety%20component%20braking%20system&width=600&height=400&seq=brakedisc001&orientation=landscape'
    }
  ];

  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contatti');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black shadow-lg">
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 sm:h-12 md:h-14 w-auto"
              />
            </Link>
            <Link 
              to="/" 
              className="flex items-center text-white hover:text-[#E31E24] transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              <span className="font-semibold text-sm">Torna alla Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <div className="inline-block mb-4">
            <span className="inline-block px-6 py-2 border-2 border-[#E31E24] text-[#E31E24] bg-white font-bold text-xs tracking-widest rounded-full uppercase">
              Sicurezza Stradale
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-6">
            Impianto Frenante
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            La sicurezza inizia dai freni. Scopri i componenti fondamentali dell'impianto frenante e l'importanza di una manutenzione regolare per viaggiare sempre in sicurezza.
          </p>
        </div>
      </section>

      {/* Brake Components Detail */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="space-y-16 md:space-y-24">
            {brakeItems.map((item, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                    <div className="w-full h-64 sm:h-80 md:h-96">
                      <img 
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#E31E24] flex items-center justify-center mr-4">
                      <i className="ri-speed-line text-white text-2xl"></i>
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black">
                      {item.title}
                    </h2>
                  </div>
                  <p className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed">
                    {item.description}
                  </p>
                  <ul className="space-y-3">
                    {item.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-[#E31E24]/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-[#E31E24] text-sm"></i>
                        </div>
                        <span className="text-gray-700 text-sm sm:text-base">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl p-5 sm:p-8 md:p-12 border-l-4 border-[#E31E24]">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#E31E24]/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-[#E31E24] text-2xl md:text-3xl"></i>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-black mb-3 md:mb-4">
                  Segnali di Usura dei Freni
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div className="flex items-center">
                    <i className="ri-volume-up-line text-[#E31E24] mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700 text-xs sm:text-sm">Rumori stridenti durante la frenata</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-shake-hands-line text-[#E31E24] mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700 text-xs sm:text-sm">Vibrazioni al volante o al pedale</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-arrow-down-line text-[#E31E24] mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700 text-xs sm:text-sm">Pedale del freno troppo morbido</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-route-line text-[#E31E24] mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700 text-xs sm:text-sm">Veicolo che tira da un lato</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            Controlla i Tuoi Freni
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto">
            Non aspettare che sia troppo tardi. Contattaci per un controllo gratuito dell'impianto frenante direttamente a domicilio.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={scrollToContact}
              className="inline-flex items-center bg-[#E31E24] text-white font-bold text-sm px-8 py-4 rounded-full hover:bg-[#c41a1f] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <span className="mr-3">RICHIEDI PREVENTIVO</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
            <a 
              href="https://wa.me/393518312394"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-black font-bold text-sm px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-[#25D366] text-xl mr-3"></i>
              <span>WHATSAPP</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#E31E24] py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col items-center space-y-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center">
              <i className="ri-mail-line text-white text-lg mr-2"></i>
              <a href="mailto:metamorfosi.preparazioni@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300 cursor-pointer text-xs sm:text-sm break-all">
                metamorfosi.preparazioni@gmail.com
              </a>
            </div>
            <p className="text-white/80 text-xs sm:text-sm text-center">
              © 2025 Metamorfosi Preparazioni. Tutti i diritti riservati.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/70 hover:text-white text-xs sm:text-sm transition-colors duration-300 cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
